<!-- eslint-disable max-len -->
<!-- eslint-disable vuejs-accessibility/label-has-for -->
<template>
  <!-- Loading -->
  <div
    v-if="isLoading"
    class="min-h-screen bg-gray-100 flex items-center justify-center"
  >
    <div class="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-teal-600" />
  </div>

  <!-- Create Username — Onboarding (no bio exists yet) -->
  <div
    v-else-if="!hasBio"
    class="flex min-h-screen flex-1 bg-gray-100"
  >
    <!-- Left: Form panel -->
    <div
      class="relative flex flex-1 flex-col justify-center px-6 py-8 sm:px-6 sm:py-12 lg:flex-none lg:px-12 xl:px-20 md:min-w-[50vw]"
    >
      <!-- Back button -->
      <button
        class="absolute top-4 left-4 flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors z-10"
        @click="router.push({ name: 'DashboardPage' })"
      >
        <ArrowLeftIcon class="size-4" />
        {{ t('profile.personal.cancel') }}
      </button>

      <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-md">

        <!-- Heading — bold, aspirational -->
        <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight">
          {{ t('bio.create.headline') }}
        </h1>
        <p class="mt-3 text-base text-gray-500 leading-relaxed">
          {{ t('bio.create.subheadline') }}
        </p>

        <!-- Value props — compact, punchy -->
        <div class="mt-8 space-y-4">
          <div class="flex items-center gap-3">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
              <LinkIcon class="size-5" />
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900">
                {{ t('bio.create.feature1Title') }}
              </p>
              <p class="text-xs text-gray-500">
                {{ t('bio.create.feature1Desc') }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
              <ShoppingBagIcon class="size-5" />
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900">
                {{ t('bio.create.feature2Title') }}
              </p>
              <p class="text-xs text-gray-500">
                {{ t('bio.create.feature2Desc') }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <div class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
              <SparklesIcon class="size-5" />
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900">
                {{ t('bio.create.feature3Title') }}
              </p>
              <p class="text-xs text-gray-500">
                {{ t('bio.create.feature3Desc') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Form card -->
        <div class="mt-8 rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 border-t-4 border-teal-500 px-6 py-8 sm:px-8">
          <label
            for="bio-username"
            class="block text-sm font-medium text-gray-900 mb-2"
          >
            {{ t('bio.create.chooseUsername') }}
          </label>
          <div class="flex items-center rounded-lg border border-gray-200 bg-gray-50 overflow-hidden focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-500 transition-colors">
            <span class="pl-3 text-sm text-gray-400 select-none whitespace-nowrap">@</span>
            <input
              id="bio-username"
              v-model="newUsername"
              type="text"
              maxlength="30"
              :placeholder="t('bio.create.usernamePlaceholder')"
              class="flex-1 bg-transparent px-2 py-2.5 text-sm outline-none text-gray-900 placeholder-gray-400"
            >
          </div>
          <p class="text-xs text-gray-400 mt-2 mb-6">
            {{ newUsername.length }}/30 · {{ t('bio.create.usernameHint') }}
          </p>

          <button
            class="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isCreating || newUsername.length < 3"
            @click="createBio"
          >
            <svg
              v-if="isCreating"
              class="h-4 w-4 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            {{ t('bio.create.create') }}
          </button>
        </div>

        <p class="mt-4 text-center text-xs text-gray-400">
          livelink.com.br/@{{ newUsername || '...' }}
        </p>
      </div>
    </div>

    <!-- Right: Image panel (fixed, same as session pages) -->
    <div class="hidden w-0 flex-1 lg:block">
      <div class="fixed inset-y-0 right-0 w-[50vw]">
        <img
          class="absolute inset-0 size-full object-cover"
          src="@/assets/images/backgrounds/redhead-woman-in-green.png"
          loading="lazy"
          alt="Woman with products"
        >
        <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10" />
        <div class="absolute bottom-8 left-8 right-8">
          <p class="text-white/90 text-lg font-semibold drop-shadow-lg">
            LIVELINK
          </p>
          <p class="text-white/70 text-sm mt-1 drop-shadow">
            {{ randomTagline }}
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Manager (bio exists) -->
  <div v-else>
    <RouterView
      v-model="myBio"
      @update:my-bio="reload"
    />
  </div>
</template>

<script setup lang="ts">
import {
  ref, computed, onBeforeMount,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import useUserStore from '@/store/user';
import UserBio from '@/models/user/bio/userBio';
import Profile from '@/models/user/bio/profile';
import Style from '@/models/user/bio/style';
import {
  ArrowLeftIcon,
  LinkIcon,
  ShoppingBagIcon,
  SparklesIcon,
} from '@heroicons/vue/24/outline';

const router = useRouter();
const { t, tm } = useI18n();
const toast = useToast();
const {
  usersDoLoadBio,
  usersDoGetMyUser,
  usersDoUpdateMyUser,
  usersDoUpdateMyBio,
} = useUserStore();

/*
  Data
*/
const isLoading = ref(true);
const isCreating = ref(false);
const myProfile = ref<any>({});
const myBio = ref<any>({});
const newUsername = ref('');

/*
  Computed
*/
const hasUsername = computed(
  () => !!myProfile.value?.username,
);
const hasBio = computed(
  () => hasUsername.value && !!myBio.value?.id,
);

const randomTagline = computed(() => {
  const list = tm('session.taglines');
  return Array.isArray(list)
    ? list[Math.floor(Math.random() * list.length)]
    : '';
});

/*
  Methods
*/
const load = async () => {
  isLoading.value = true;
  try {
    // Step 1: Get user profile
    const profile = await usersDoGetMyUser();
    if (!profile) {
      router.push({ name: 'DashboardPage' });
      return;
    }
    myProfile.value = profile;

    // Step 2: If user has a username, load or auto-create bio
    if (profile.username) {
      const cleanUsername = profile.username.replace('@', '');
      let bio = null;

      try {
        bio = await usersDoLoadBio(cleanUsername);
      } catch {
        // 404 — bio document doesn't exist in MongoDB
      }

      if (bio) {
        // Bio exists — use it
        myBio.value = bio;
      } else {
        // Username exists but no bio doc — auto-create it
        const newBio = new UserBio({
          username: cleanUsername,
          style: Style.newStyle(),
          live: null,
          profile: Profile.newProfile(profile.name || cleanUsername),
          links: [],
        });
        try {
          await usersDoUpdateMyBio(newBio);
          // Re-load to get the MongoDB _id
          const created = await usersDoLoadBio(cleanUsername);
          myBio.value = created || newBio;
        } catch {
          // If auto-create fails, show empty state
          myBio.value = newBio;
        }
      }
    }
    // If no username, hasBio = false → shows onboarding
  } catch (err: any) {
    console.error('Bio load error:', err);
    if (err?.response?.status === 401) {
      router.push({ name: 'SessionLoginPage' });
    }
  } finally {
    isLoading.value = false;
  }
};

const createBio = async () => {
  isCreating.value = true;
  try {
    const username = newUsername.value.trim().toLowerCase();

    // Step 1: Save username to user profile (postgres)
    await usersDoUpdateMyUser({
      username: `@${username}`,
    });

    // Step 2: Create bio document in MongoDB
    const newBio = new UserBio({
      username,
      style: Style.newStyle(),
      live: null,
      profile: Profile.newProfile(myProfile.value?.name || username),
      links: [],
    });
    await usersDoUpdateMyBio(newBio);

    // Step 3: Refresh state so myBio.value picks up the persisted doc with its _id
    toast.success(t('bio.saved'));
    await load();
  } catch (err: any) {
    const msg = err?.response?.data?.error
      || t('bio.saveError');
    toast.error(msg);
    isCreating.value = false;
  }
};

const reload = async () => {
  await load();
};

/*
  Hooks
*/
onBeforeMount(() => {
  load();
});
</script>
